import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { RefreshCw, Plus, X, AlertTriangle, Tag } from 'lucide-react'
import { useAuth } from '../../../../context/AuthContext'
import ZoneEditor from './ZoneEditor'
import BulkTagsModal from '../shared/BulkTagsModal'
import '../SponsorManager.css'

function zoneField(z, ...keys) {
  for (const k of keys) {
    if (z?.[k] != null) return z[k]
  }
  return ''
}

// AdSpeed's full lifecycle: active → pending → archived → deleted.
const STATUS_TABS = [
  { value: 'active',   label: 'Active' },
  { value: 'pending',  label: 'Pending' },
  { value: 'archived', label: 'Archived' },
  { value: 'deleted',  label: 'Deleted' },
]

export default function ZonesList() {
  const { api } = useAuth()
  const navigate = useNavigate()
  const [zones, setZones] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [stale, setStale] = useState(false)
  const [search, setSearch] = useState('')
  const [statusTab, setStatusTab] = useState('active')
  const [status, setStatus] = useState('')
  const [showCreate, setShowCreate] = useState(false)

  // Selection / bulk state
  const [selected, setSelected] = useState(new Set())
  const [bulkRunning, setBulkRunning] = useState(false)
  const [showBulkTags, setShowBulkTags] = useState(false)

  // Tag filter + sort
  const [tagFilter, setTagFilter] = useState('')
  const [allTbtvTags, setAllTbtvTags] = useState([])
  const [tagFilteredIds, setTagFilteredIds] = useState(null)
  const [sortBy, setSortBy] = useState('id')
  const [sortDir, setSortDir] = useState('asc')

  function handleSort(col) {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortBy(col); setSortDir('asc') }
  }

  const loadZones = useCallback(async () => {
    try {
      const data = await api(`/sponsors/zones?status=${statusTab}`)
      setZones(data.zones || [])
      setStale(!!data.stale)
      if (data.ok === false) {
        const msg = data.error?.message || 'AdSpeed rejected the filter'
        const isDeleted = statusTab === 'deleted'
        setStatus(isDeleted
          ? `AdSpeed's getList API doesn't list deleted zones. The dashboard shows counts but not the items.`
          : `Error: ${msg}`)
      } else {
        setStatus('')
      }
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }, [api, statusTab])

  useEffect(() => {
    setLoading(true)
    loadZones().finally(() => setLoading(false))
    setSelected(new Set())
  }, [loadZones])

  useEffect(() => {
    api('/tags').then(d => setAllTbtvTags(d.tags || [])).catch(() => {})
  }, [api])

  useEffect(() => {
    if (!tagFilter) {
      setTagFilteredIds(null)
      return
    }
    api(`/sponsors/zones/by-tag?tag=${encodeURIComponent(tagFilter)}`)
      .then(d => {
        const ids = new Set((d.zones || []).map(z => String(z.id ?? z.ZoneId ?? z.zone_id)))
        setTagFilteredIds(ids)
      })
      .catch(e => setStatus(`Tag filter error: ${e.message}`))
  }, [api, tagFilter])

  async function handleRefresh() {
    setRefreshing(true)
    try {
      await api('/sponsors/cache/bust?prefix=zones:', { method: 'POST' })
      await loadZones()
      setStatus('Refreshed from AdSpeed')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setRefreshing(false)
  }

  async function handleCreate(body) {
    try {
      const res = await api('/sponsors/zones', { method: 'POST', body: JSON.stringify(body) })
      if (res?.ok === false) {
        throw new Error(res.error?.message || 'AdSpeed rejected the request')
      }
      setShowCreate(false)
      setStatus('Zone created')
      await loadZones()
    } catch (e) {
      throw e
    }
  }

  // Apply text search + tag filter (intersection).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return zones.filter(z => {
      const id = String(zoneField(z, 'id', 'ZoneId', 'zone_id') || '')
      const name = String(zoneField(z, 'name', 'Name', 'zone_name') || '').toLowerCase()
      if (q && !name.includes(q) && !id.toLowerCase().includes(q)) return false
      if (tagFilteredIds && !tagFilteredIds.has(id)) return false
      return true
    })
  }, [zones, search, tagFilteredIds])

  // Sort by selected column.
  const sorted = useMemo(() => {
    const arr = [...filtered]
    arr.sort((a, b) => {
      let av, bv
      if (sortBy === 'id') {
        av = Number(zoneField(a, 'id', 'ZoneId', 'zone_id') || 0)
        bv = Number(zoneField(b, 'id', 'ZoneId', 'zone_id') || 0)
      } else {
        av = String(zoneField(a, sortBy, sortBy.charAt(0).toUpperCase() + sortBy.slice(1)) || '').toLowerCase()
        bv = String(zoneField(b, sortBy, sortBy.charAt(0).toUpperCase() + sortBy.slice(1)) || '').toLowerCase()
      }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0
      return sortDir === 'asc' ? cmp : -cmp
    })
    return arr
  }, [filtered, sortBy, sortDir])

  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleSelectAll() {
    const allIds = sorted.map(z => String(zoneField(z, 'id', 'ZoneId', 'zone_id')))
    if (allIds.every(id => selected.has(id))) {
      setSelected(new Set())
    } else {
      setSelected(new Set(allIds))
    }
  }

  // NOTE: zone status changes are NOT supported by AdSpeed's API.
  // AS.Zone.edit accepts: name, secondary, optimizer, descr — no `status`
  // field. Archiving/activating zones must be done in AdSpeed's dashboard.
  // (Ads have their own AS.Ad.edit which DOES accept status — see AdsList.)

  // Bulk tbtv-tags — loops through selection, calling PUT /tbtv-tags per zone.
  async function handleBulkTags(tags, mode) {
    const ids = Array.from(selected)
    setBulkRunning(true)
    let ok = 0, fail = 0
    for (const id of ids) {
      try {
        let finalTags = tags
        if (mode === 'add') {
          const existing = await api(`/sponsors/zones/${id}/tbtv-tags`)
          const existingNames = (existing?.tags || []).map(t => t.name)
          finalTags = Array.from(new Set([...existingNames, ...tags]))
        }
        await api(`/sponsors/zones/${id}/tbtv-tags`, {
          method: 'PUT',
          body: JSON.stringify({ tags: finalTags }),
        })
        ok++
      } catch {
        fail++
      }
    }
    setBulkRunning(false)
    setShowBulkTags(false)
    setSelected(new Set())
    setStatus(fail === 0
      ? `Tagged ${ok} zone(s).`
      : `Tagged ${ok}, ${fail} failed.`)
  }

  const allVisibleSelected = sorted.length > 0
    && sorted.every(z => selected.has(String(zoneField(z, 'id', 'ZoneId', 'zone_id'))))

  return (
    <div className="sponsors-page">
      <div className="sponsors-header">
        <div className="sponsors-header-left">
          <h1>Zones</h1>
          <span className="sponsors-count">{filtered.length} of {zones.length}</span>
          {stale && (
            <span className="sponsors-stale-badge">
              <AlertTriangle size={11} /> Stale cache
            </span>
          )}
        </div>
        <div className="sponsors-header-right">
          <button
            className="sponsors-refresh-btn"
            onClick={handleRefresh}
            disabled={refreshing || loading}
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={14} /> New zone
          </button>
        </div>
      </div>

      {status && (
        <div className={`sponsors-status ${status.startsWith('Error') ? 'error' : 'success'}`}>
          <span>{status}</span>
          <button onClick={() => setStatus('')}><X size={14} /></button>
        </div>
      )}

      <div className="adtag-format-row" style={{ marginBottom: 10 }}>
        {STATUS_TABS.map(t => (
          <button
            key={t.value}
            className={`adtag-format-btn ${statusTab === t.value ? 'active' : ''}`}
            onClick={() => setStatusTab(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="zones-toolbar">
        <input
          className="zones-search"
          placeholder="Filter by name or id…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          value={tagFilter}
          onChange={e => setTagFilter(e.target.value)}
          className="zones-search"
          style={{ flex: '0 0 180px' }}
        >
          <option value="">All tags</option>
          {allTbtvTags.map(t => <option key={t} value={t}>tag: {t}</option>)}
        </select>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div style={{
          display: 'flex', gap: 10, alignItems: 'center',
          padding: '10px 14px', background: '#1e3a8a30', color: '#93c5fd',
          borderRadius: 6, marginBottom: 10, flexWrap: 'wrap',
        }}>
          <strong>{selected.size} selected</strong>
          <button
            className="sponsors-refresh-btn"
            onClick={() => setShowBulkTags(true)}
            disabled={bulkRunning}
          >
            <Tag size={13} /> Apply tags
          </button>
          <span className="muted" style={{ fontSize: '0.75rem' }}>
            (Zone status changes must be done in AdSpeed's dashboard — their
            API doesn't accept status on zone edit.)
          </span>
          <button
            className="sponsors-refresh-btn"
            onClick={() => setSelected(new Set())}
            disabled={bulkRunning}
            style={{ marginLeft: 'auto' }}
          >
            <X size={13} /> Clear
          </button>
          {bulkRunning && <span className="muted" style={{ fontSize: '0.8rem' }}>Working…</span>}
        </div>
      )}

      {loading ? (
        <div className="muted">Loading zones…</div>
      ) : sorted.length === 0 ? (
        <div className="muted">No zones found.</div>
      ) : (
        <table className="zones-table">
          <thead>
            <tr>
              <th style={{ width: 32 }}>
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAll}
                  title="Select all visible"
                />
              </th>
              {[
                { key: 'id',     label: 'ID' },
                { key: 'name',   label: 'Name' },
                { key: 'status', label: 'Status' },
              ].map(c => (
                <th
                  key={c.key}
                  onClick={() => handleSort(c.key)}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Click to sort"
                >
                  {c.label}
                  {sortBy === c.key && <span style={{ marginLeft: 4, color: '#f59e0b' }}>
                    {sortDir === 'asc' ? '↑' : '↓'}
                  </span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map(z => {
              const id = String(zoneField(z, 'id', 'ZoneId', 'zone_id'))
              const isSel = selected.has(id)
              return (
                <tr
                  key={id}
                  onClick={() => navigate(`/sponsors/zones/${id}`)}
                  style={isSel ? { background: '#1e3a8a30' } : undefined}
                >
                  <td onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggleSelect(id)}
                    />
                  </td>
                  <td>{id}</td>
                  <td>{zoneField(z, 'name', 'Name', 'zone_name')}</td>
                  <td>{zoneField(z, 'status', 'Status')}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      {showCreate && (
        <ZoneEditor
          onClose={() => setShowCreate(false)}
          onSubmit={handleCreate}
        />
      )}

      {showBulkTags && (
        <BulkTagsModal
          count={selected.size}
          entityLabel="zone"
          onClose={() => setShowBulkTags(false)}
          onSubmit={handleBulkTags}
        />
      )}
    </div>
  )
}
